import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import useReportFiltersState from 'pages/reports/hooks/useReportFiltersState'
import usePersistedTransactionFilters from 'pages/transactions/hooks/usePersistedTransactionFilters'

import useClearQueryParams from 'hooks/useClearQueryParams'

import { useLocalStorage } from '../hooks/useLocalStorage'

type OrgContextValue = {
  org?: string
  setOrg: (org: string) => void
}

export const OrgContext = createContext<OrgContextValue>({
  org: undefined,
  setOrg: () => undefined,
})

export function useOrg() {
  return useContext(OrgContext)
}

export const OrgProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [org, setOrg] = useLocalStorage('currentOrg')
  const { resetReportFilterState } = useReportFiltersState()
  const { resetTransactionstFilterState } = usePersistedTransactionFilters()
  const { clearQueryParams } = useClearQueryParams()
  const lastOrgRef = useRef(org)

  const navigate = useNavigate()

  /**
   * Every time we switch organizations, reload the page to reset client-side state. This is
   * the easiest way to ensure we don't persist any data such as query caches or component state.
   */
  useEffect(() => {
    if (org && org !== lastOrgRef.current) {
      resetReportFilterState()
      resetTransactionstFilterState()
      clearQueryParams()
      navigate(0)
    }
  }, [clearQueryParams, navigate, org, resetReportFilterState, resetTransactionstFilterState])

  const value = useMemo(
    () => ({
      org,
      setOrg,
    }),
    [org, setOrg]
  )

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>
}
